import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useEffect, useRef } from 'react';
import type { Claim } from '@/core/types';
import { CertCard } from './CertCard';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface CertGridProps {
  certs: Claim[];
  highlightedId: string | null;
  onCardClick: (claim: Claim) => void;
}

export function CertGrid({ certs, highlightedId, onCardClick }: CertGridProps) {
  const highlightedRef = useRef<HTMLDivElement>(null);

  // ハイライトされたカードにスクロール
  useEffect(() => {
    if (highlightedId && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedId]);

  if (certs.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          完走証がまだありません
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {certs.map((cert, index) => (
        <Grid
          key={cert.id}
          size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
          sx={{
            animation: `${fadeInUp} 0.6s ease-out ${0.05 + index * 0.02}s both`,
          }}
        >
          <CertCard
            ref={cert.id === highlightedId ? highlightedRef : undefined}
            claim={cert}
            highlighted={cert.id === highlightedId}
            onClick={() => onCardClick(cert)}
          />
        </Grid>
      ))}
    </Grid>
  );
}
